import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Clock, X } from 'lucide-react'
import api from '../lib/api'
import toast from 'react-hot-toast'
import type { LabOrder } from '../types/lab'

interface LabNotificationState {
  readyCount: number
  shippedCount: number
  urgentCount: number
  overdueCount: number
}

const NOTIF_STORAGE_KEY = 'lab-notifications-last-check'

function getLastCheck(): string {
  return localStorage.getItem(NOTIF_STORAGE_KEY) ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
}

function setLastCheck(date: string) {
  localStorage.setItem(NOTIF_STORAGE_KEY, date)
}

export function useLabNotifications(enabled: boolean = true) {
  const lastCheckRef = useRef<string>(getLastCheck())
  const shownRef = useRef<Set<string>>(new Set())

  const { data: orders } = useQuery({
    queryKey: ['lab-orders', 'notifications'],
    queryFn: async () => {
      const { data } = await api.get('/lab/orders', {
        params: { status: '', limit: 200 },
      })
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.orders)
            ? data.orders
            : []
      return list as LabOrder[]
    },
    enabled,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: true,
    staleTime: 30 * 1000,
  })

  const state: LabNotificationState = {
    readyCount: orders?.filter((o) => o.status === 'ready').length ?? 0,
    shippedCount: orders?.filter((o) => o.status === 'shipped').length ?? 0,
    urgentCount: orders?.filter((o) => o.priority === 'urgent' || o.priority === 'express').length ?? 0,
    overdueCount: orders?.filter((o) => {
      if (!o.deadline || ['received', 'delivered', 'cancelled'].includes(o.status)) return false
      return new Date(o.deadline) < new Date()
    }).length ?? 0,
  }

  useEffect(() => {
    if (!orders || orders.length === 0) return

    const lastCheck = new Date(lastCheckRef.current)
    const newReady = orders.filter(
      (o) => o.status === 'ready' && new Date(o.updatedAt) > lastCheck
    )
    const newShipped = orders.filter(
      (o) => o.status === 'shipped' && new Date(o.updatedAt) > lastCheck
    )
    const urgentOverdue = orders.filter(
      (o) => {
        if (!o.deadline || ['received', 'delivered', 'cancelled'].includes(o.status)) return false
        const hoursUntil = (new Date(o.deadline).getTime() - Date.now()) / (1000 * 60 * 60)
        return hoursUntil <= 24 && hoursUntil > 0
      }
    )

    // Show toast for newly ready orders
    for (const order of newReady) {
      const toastId = `lab-ready-${order.id}`
      if (!shownRef.current.has(toastId)) {
        shownRef.current.add(toastId)
        toast.success(
          `Orden ${order.orderNumber} lista en laboratorio — ${order.patientName}`,
          {
            id: toastId,
            duration: 8000,
            icon: '✅',
          }
        )
      }
    }

    // Show toast for newly shipped orders
    for (const order of newShipped) {
      const toastId = `lab-shipped-${order.id}`
      if (!shownRef.current.has(toastId)) {
        shownRef.current.add(toastId)
        toast(
          `Orden ${order.orderNumber} enviada por laboratorio — ${order.patientName}`,
          {
            id: toastId,
            duration: 8000,
            icon: '📦',
          }
        )
      }
    }

    // Show toast for urgent deadlines (once per session)
    for (const order of urgentOverdue) {
      const toastId = `lab-urgent-${order.id}`
      if (!shownRef.current.has(toastId)) {
        shownRef.current.add(toastId)
        toast.custom(
          (t) => (
            <div
              className={`max-w-md w-full bg-surface-1 border border-error-500 rounded-xl shadow-xl p-4 flex items-start gap-3 ${
                t.visible ? 'animate-enter' : 'animate-leave'
              }`}
            >
              <div className="p-2 rounded-lg bg-error-50 text-error-600 flex-shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary">
                  Orden {order.orderNumber} vence pronto
                </p>
                <p className="text-xs text-text-secondary mt-1">
                  {order.patientName}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="text-xs font-medium text-text-secondary px-3 py-1.5 rounded-lg hover:bg-surface-2"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-text-tertiary hover:text-text-primary flex-shrink-0"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ),
          {
            id: toastId,
            duration: 10000,
            position: 'top-right',
          }
        )
      }
    }

    setLastCheck(new Date().toISOString())
    lastCheckRef.current = new Date().toISOString()
  }, [orders])

  return {
    state,
    orders,
    totalAlerts: state.readyCount + state.shippedCount + state.overdueCount,
  }
}
